import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../ThemeContext';
import ThemeToggle from '../components/ThemeToggle';
import Sidebar from '../components/Sidebar';
import { UserPlus, Mail, Lock, User, ArrowLeft, CreditCard } from 'lucide-react';
import './Settings.css';

function CreateClient() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    plan: 'Starter'
  });

  // Plan limits configuration
  const planLimits = {
    'Starter': { events: 5, attendees: 500 },
    'Professional': { events: 20, attendees: 2000 },
    'Enterprise': { events: null, attendees: null } // null = unlimited
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.fullName || !formData.email || !formData.password) {
      setError('All fields are required');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('https://api.eventpiclive.com/api/admin/create-client', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken') || localStorage.getItem('token')}`
        },
        credentials: 'include',
        body: JSON.stringify({
          full_name: formData.fullName,
          email: formData.email,
          password: formData.password,
          plan: formData.plan,
          events_limit: planLimits[formData.plan].events,
          attendees_limit: planLimits[formData.plan].attendees
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create client account');
      }

      setSuccess(`Client account created successfully! Login credentials have been set.`);
      setFormData({
        fullName: '',
        email: '',
        password: '',
        confirmPassword: ''
      });

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="page-container">
      <ThemeToggle />
      <Sidebar onLogout={() => { localStorage.clear(); navigate('/'); }} isSuperAdmin={true} />
      
      <div className="main-content" style={{ marginLeft: '260px', padding: '40px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          {/* Header */}
          <div style={{ marginBottom: '32px' }}>
            <button
              onClick={() => navigate('/admin/clients')}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                marginBottom: '16px',
                padding: '8px 0'
              }}
            >
              <ArrowLeft size={18} />
              Back to Clients
            </button>
            
            <h1 style={{
              fontSize: '32px',
              fontWeight: '700',
              color: 'var(--text)',
              margin: '0 0 8px 0'
            }}>
              Create Client Account
            </h1>
            <p style={{
              color: 'var(--text-secondary)',
              margin: 0,
              fontSize: '16px'
            }}>
              Manually create a new admin account for a client
            </p>
          </div>

          {/* Success Message */}
          {success && (
            <div style={{
              background: '#d1fae5',
              border: '1px solid #6ee7b7',
              color: '#065f46',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '24px',
              fontSize: '14px'
            }}>
              {success}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div style={{
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              color: '#991b1b',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '24px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {/* Form */}
          <div style={{
            background: 'var(--card-bg)',
            borderRadius: '12px',
            padding: '32px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <form onSubmit={handleSubmit}>
              {/* Full Name */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: '600',
                  color: 'var(--text)',
                  marginBottom: '8px',
                  fontSize: '14px'
                }}>
                  <User size={18} />
                  Full Name
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="John Doe"
                  required
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '16px',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    background: 'var(--input-bg)',
                    color: 'var(--text)',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                />
              </div>

              {/* Plan Selection */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: '600',
                  color: 'var(--text)',
                  marginBottom: '8px',
                  fontSize: '14px'
                }}>
                  <CreditCard size={18} />
                  Subscription Plan
                </label>
                <select
                  name="plan"
                  value={formData.plan}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '16px',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    background: 'var(--input-bg)',
                    color: 'var(--text)',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    cursor: 'pointer'
                  }}
                >
                  <option value="Starter">Starter - 5 events/month, 500 attendees/event</option>
                  <option value="Professional">Professional - 20 events/month, 2000 attendees/event</option>
                  <option value="Enterprise">Enterprise - Unlimited events & attendees</option>
                </select>
                <p style={{
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                  marginTop: '6px',
                  marginBottom: 0
                }}>
                  {formData.plan === 'Starter' && '5 events per month, 500 attendees per event'}
                  {formData.plan === 'Professional' && '20 events per month, 2,000 attendees per event'}
                  {formData.plan === 'Enterprise' && 'Unlimited events and attendees'}
                </p>
              </div>

              {/* Email */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: '600',
                  color: 'var(--text)',
                  marginBottom: '8px',
                  fontSize: '14px'
                }}>
                  <Mail size={18} />
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  required
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '16px',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    background: 'var(--input-bg)',
                    color: 'var(--text)',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                />
              </div>

              {/* Password */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: '600',
                  color: 'var(--text)',
                  marginBottom: '8px',
                  fontSize: '14px'
                }}>
                  <Lock size={18} />
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Minimum 8 characters"
                  required
                  disabled={loading}
                  minLength={8}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '16px',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    background: 'var(--input-bg)',
                    color: 'var(--text)',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                />
              </div>

              {/* Confirm Password */}
              <div style={{ marginBottom: '32px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: '600',
                  color: 'var(--text)',
                  marginBottom: '8px',
                  fontSize: '14px'
                }}>
                  <Lock size={18} />
                  Confirm Password
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter password"
                  required
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: '16px',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    background: 'var(--input-bg)',
                    color: 'var(--text)',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '14px 24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'white',
                  background: loading ? '#9ca3af' : '#3B82F6',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <UserPlus size={20} />
                {loading ? 'Creating Account...' : 'Create Client Account'}
              </button>
            </form>
          </div>
        </div>
      </div>

      
    </div>
  );
}

export default CreateClient;
